/*

https://github.com/etienne-martin/WebCrypto.swift

MIT License

Copyright (c) 2017 Etienne Martin

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

*/

interface AESKeyStruct {
  key: string
  iv: string
  salt: string
}

class ByteStringBuffer {
  private readonly data: string = ''
  private read: number = 0

  constructor(b) {
    if (typeof b === 'string') {
      this.data = b
    } else if (b instanceof ByteStringBuffer || (typeof b === 'object' && typeof b.data === 'string' && typeof b.read === 'number')) {
      // copy existing buffer
      this.data = b.data
      this.read = b.read
    }
  }

  getBytes(count) {
    let rval
    if (count) {
      // read count bytes
      count = Math.min(this.length(), count)
      rval = this.data.slice(this.read, this.read + count)
      this.read += count
    } else if (count === 0) {
      rval = ''
    } else {
      // read all bytes, optimize to only copy when needed
      rval = (this.read === 0) ? this.data : this.data.slice(this.read)
    }
    return rval
  }

  length() {
    return this.data.length - this.read
  }
}

export class Webcrypto {
  private hexChr: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'a', 'b', 'c', 'd', 'e', 'f']
  private cryptoSubtle
  private saltPrefix = 'Salted__'

  constructor() {
    // @ts-ignore
    if (window.crypto && !window.crypto.subtle && window.crypto.webkitSubtle) {
      // @ts-ignore
      this.cryptoSubtle = window.crypto.webkitSubtle
    } else {
      this.cryptoSubtle = window.crypto.subtle
    }
  }

  private md5cycle(x, k) {
    let a = x[0]
    let b = x[1]
    let c = x[2]
    let d = x[3]

    a += (b & c | ~b & d) + k[0] - 680876936 | 0
    a = (a << 7 | a >>> 25) + b | 0
    d += (a & b | ~a & c) + k[1] - 389564586 | 0
    d = (d << 12 | d >>> 20) + a | 0
    c += (d & a | ~d & b) + k[2] + 606105819 | 0
    c = (c << 17 | c >>> 15) + d | 0
    b += (c & d | ~c & a) + k[3] - 1044525330 | 0
    b = (b << 22 | b >>> 10) + c | 0
    a += (b & c | ~b & d) + k[4] - 176418897 | 0
    a = (a << 7 | a >>> 25) + b | 0
    d += (a & b | ~a & c) + k[5] + 1200080426 | 0
    d = (d << 12 | d >>> 20) + a | 0
    c += (d & a | ~d & b) + k[6] - 1473231341 | 0
    c = (c << 17 | c >>> 15) + d | 0
    b += (c & d | ~c & a) + k[7] - 45705983 | 0
    b = (b << 22 | b >>> 10) + c | 0
    a += (b & c | ~b & d) + k[8] + 1770035416 | 0
    a = (a << 7 | a >>> 25) + b | 0
    d += (a & b | ~a & c) + k[9] - 1958414417 | 0
    d = (d << 12 | d >>> 20) + a | 0
    c += (d & a | ~d & b) + k[10] - 42063 | 0
    c = (c << 17 | c >>> 15) + d | 0
    b += (c & d | ~c & a) + k[11] - 1990404162 | 0
    b = (b << 22 | b >>> 10) + c | 0
    a += (b & c | ~b & d) + k[12] + 1804603682 | 0
    a = (a << 7 | a >>> 25) + b | 0
    d += (a & b | ~a & c) + k[13] - 40341101 | 0
    d = (d << 12 | d >>> 20) + a | 0
    c += (d & a | ~d & b) + k[14] - 1502002290 | 0
    c = (c << 17 | c >>> 15) + d | 0
    b += (c & d | ~c & a) + k[15] + 1236535329 | 0
    b = (b << 22 | b >>> 10) + c | 0

    a += (b & d | c & ~d) + k[1] - 165796510 | 0
    a = (a << 5 | a >>> 27) + b | 0
    d += (a & c | b & ~c) + k[6] - 1069501632 | 0
    d = (d << 9 | d >>> 23) + a | 0
    c += (d & b | a & ~b) + k[11] + 643717713 | 0
    c = (c << 14 | c >>> 18) + d | 0
    b += (c & a | d & ~a) + k[0] - 373897302 | 0
    b = (b << 20 | b >>> 12) + c | 0
    a += (b & d | c & ~d) + k[5] - 701558691 | 0
    a = (a << 5 | a >>> 27) + b | 0
    d += (a & c | b & ~c) + k[10] + 38016083 | 0
    d = (d << 9 | d >>> 23) + a | 0
    c += (d & b | a & ~b) + k[15] - 660478335 | 0
    c = (c << 14 | c >>> 18) + d | 0
    b += (c & a | d & ~a) + k[4] - 405537848 | 0
    b = (b << 20 | b >>> 12) + c | 0
    a += (b & d | c & ~d) + k[9] + 568446438 | 0
    a = (a << 5 | a >>> 27) + b | 0
    d += (a & c | b & ~c) + k[14] - 1019803690 | 0
    d = (d << 9 | d >>> 23) + a | 0
    c += (d & b | a & ~b) + k[3] - 187363961 | 0
    c = (c << 14 | c >>> 18) + d | 0
    b += (c & a | d & ~a) + k[8] + 1163531501 | 0
    b = (b << 20 | b >>> 12) + c | 0
    a += (b & d | c & ~d) + k[13] - 1444681467 | 0
    a = (a << 5 | a >>> 27) + b | 0
    d += (a & c | b & ~c) + k[2] - 51403784 | 0
    d = (d << 9 | d >>> 23) + a | 0
    c += (d & b | a & ~b) + k[7] + 1735328473 | 0
    c = (c << 14 | c >>> 18) + d | 0
    b += (c & a | d & ~a) + k[12] - 1926607734 | 0
    b = (b << 20 | b >>> 12) + c | 0

    a += (b ^ c ^ d) + k[5] - 378558 | 0
    a = (a << 4 | a >>> 28) + b | 0
    d += (a ^ b ^ c) + k[8] - 2022574463 | 0
    d = (d << 11 | d >>> 21) + a | 0
    c += (d ^ a ^ b) + k[11] + 1839030562 | 0
    c = (c << 16 | c >>> 16) + d | 0
    b += (c ^ d ^ a) + k[14] - 35309556 | 0
    b = (b << 23 | b >>> 9) + c | 0
    a += (b ^ c ^ d) + k[1] - 1530992060 | 0
    a = (a << 4 | a >>> 28) + b | 0
    d += (a ^ b ^ c) + k[4] + 1272893353 | 0
    d = (d << 11 | d >>> 21) + a | 0
    c += (d ^ a ^ b) + k[7] - 155497632 | 0
    c = (c << 16 | c >>> 16) + d | 0
    b += (c ^ d ^ a) + k[10] - 1094730640 | 0
    b = (b << 23 | b >>> 9) + c | 0
    a += (b ^ c ^ d) + k[13] + 681279174 | 0
    a = (a << 4 | a >>> 28) + b | 0
    d += (a ^ b ^ c) + k[0] - 358537222 | 0
    d = (d << 11 | d >>> 21) + a | 0
    c += (d ^ a ^ b) + k[3] - 722521979 | 0
    c = (c << 16 | c >>> 16) + d | 0
    b += (c ^ d ^ a) + k[6] + 76029189 | 0
    b = (b << 23 | b >>> 9) + c | 0
    a += (b ^ c ^ d) + k[9] - 640364487 | 0
    a = (a << 4 | a >>> 28) + b | 0
    d += (a ^ b ^ c) + k[12] - 421815835 | 0
    d = (d << 11 | d >>> 21) + a | 0
    c += (d ^ a ^ b) + k[15] + 530742520 | 0
    c = (c << 16 | c >>> 16) + d | 0
    b += (c ^ d ^ a) + k[2] - 995338651 | 0
    b = (b << 23 | b >>> 9) + c | 0

    a += (c ^ (b | ~d)) + k[0] - 198630844 | 0
    a = (a << 6 | a >>> 26) + b | 0
    d += (b ^ (a | ~c)) + k[7] + 1126891415 | 0
    d = (d << 10 | d >>> 22) + a | 0
    c += (a ^ (d | ~b)) + k[14] - 1416354905 | 0
    c = (c << 15 | c >>> 17) + d | 0
    b += (d ^ (c | ~a)) + k[5] - 57434055 | 0
    b = (b << 21 | b >>> 11) + c | 0
    a += (c ^ (b | ~d)) + k[12] + 1700485571 | 0
    a = (a << 6 | a >>> 26) + b | 0
    d += (b ^ (a | ~c)) + k[3] - 1894986606 | 0
    d = (d << 10 | d >>> 22) + a | 0
    c += (a ^ (d | ~b)) + k[10] - 1051523 | 0
    c = (c << 15 | c >>> 17) + d | 0
    b += (d ^ (c | ~a)) + k[1] - 2054922799 | 0
    b = (b << 21 | b >>> 11) + c | 0
    a += (c ^ (b | ~d)) + k[8] + 1873313359 | 0
    a = (a << 6 | a >>> 26) + b | 0
    d += (b ^ (a | ~c)) + k[15] - 30611744 | 0
    d = (d << 10 | d >>> 22) + a | 0
    c += (a ^ (d | ~b)) + k[6] - 1560198380 | 0
    c = (c << 15 | c >>> 17) + d | 0
    b += (d ^ (c | ~a)) + k[13] + 1309151649 | 0
    b = (b << 21 | b >>> 11) + c | 0
    a += (c ^ (b | ~d)) + k[4] - 145523070 | 0
    a = (a << 6 | a >>> 26) + b | 0
    d += (b ^ (a | ~c)) + k[11] - 1120210379 | 0
    d = (d << 10 | d >>> 22) + a | 0
    c += (a ^ (d | ~b)) + k[2] + 718787259 | 0
    c = (c << 15 | c >>> 17) + d | 0
    b += (d ^ (c | ~a)) + k[9] - 343485551 | 0
    b = (b << 21 | b >>> 11) + c | 0

    x[0] = a + x[0] | 0
    x[1] = b + x[1] | 0
    x[2] = c + x[2] | 0
    x[3] = d + x[3] | 0
  }

  private md5blk(s) {
    let md5blks: string[] = []

    let i = 0
    for (; i < 64; i += 4) {
      md5blks[i >> 2] = s.charCodeAt(i) + (s.charCodeAt(i + 1) << 8) + (s.charCodeAt(i + 2) << 16) + (s.charCodeAt(i + 3) << 24)
    }
    return md5blks
  }

  private md51(s) {
    let n = s.length
    let state = [1732584193, -271733879, -1732584194, 271733878]
    let length
    let tail
    let tmp
    let lo
    let hi

    let i = 64
    for (; i <= n; i += 64) {
      this.md5cycle(state, this.md5blk(s.substring(i - 64, i)))
    }
    s = s.substring(i - 64)
    length = s.length
    tail = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    for (i = 0; i < length; i += 1) {
      tail[i >> 2] |= s.charCodeAt(i) << ((i % 4) << 3)
    }
    tail[i >> 2] |= 0x80 << ((i % 4) << 3)
    if (i > 55) {
      this.md5cycle(state, tail)
      for (i = 0; i < 16; i += 1) {
        tail[i] = 0
      }
    }

    // Beware that the final length might not fit in 32 bits so we take care of that
    tmp = n * 8
    tmp = tmp.toString(16).match(/(.*?)(.{0,8})$/)
    lo = parseInt(tmp[2], 16)
    hi = parseInt(tmp[1], 16) || 0

    tail[14] = lo
    tail[15] = hi

    this.md5cycle(state, tail)
    return state
  }

  private rhex(n) {
    let s = ''
    let j = 0
    for (; j < 4; j += 1) {
      s += this.hexChr[(n >> (j * 8 + 4)) & 0x0F] + this.hexChr[(n >> (j * 8)) & 0x0F]
    }
    return s
  }

  private hex(x) {
    let i = 0
    for (; i < x.length; i += 1) {
      x[i] = this.rhex(x[i])
    }
    return x.join('')
  }

  private hexToBinaryString(hex) {
    let bytes: number[] = []
    let length = hex.length

    let x = 0
    for (; x < length - 1; x += 2) {
      bytes.push(parseInt(hex.substr(x, 2), 16))
    }

    return String.fromCharCode.apply(String, bytes)
  }

  private hashBinary(content) {
    let hash = this.md51(content)
    let ret = this.hex(hash)

    return this.hexToBinaryString(ret)
  }

  // OpenSSL key derivation functions extracted from forge
  // https://github.com/digitalbazaar/forge
  private opensslDeriveBytes(password, salt, dkLen) {
    if (salt === null) {
      salt = ''
    }

    let digests = [this.hashBinary(password + salt)]
    let length = 16
    let i = 1

    for (; length < dkLen; ++i, length += 16) {
      digests.push(this.hashBinary(digests[i - 1] + password + salt))
    }

    return digests.join('').substr(0, dkLen)
  }

  private createBuffer(input) {
    return new ByteStringBuffer(input)
  }

  private saltShaker() {
    return this.arrayBufferToString(window.crypto.getRandomValues(new Uint8Array(8)))
  }

  private derivePassword(password, salt?): AESKeyStruct {
    let keySize = 32 // generate 32-byte (256-bit) key
    let ivSize = 16

    salt = !salt ? this.saltShaker() : salt
    let derivedBytes = this.opensslDeriveBytes(password, salt, keySize + ivSize)

    let buffer = this.createBuffer(derivedBytes)
    let key = buffer.getBytes(keySize)
    let iv = buffer.getBytes(ivSize)

    return { key: key, iv: iv, salt: salt }
  }

  private postMessage(message) {
    // Handle javascript callback
    if (typeof message.callback === 'function') {
      message.callback(message)
    } else {
      try {
        // Handle swift callback
        // @ts-ignore
        window.webkit.messageHandlers.scriptHandler.postMessage(message)
      } catch (e) {
        // do nothing
      }
    }
  }

  private hex2a(hexx) {
    let hex = hexx.toString() // force conversion
    let str = ''
    let i = 0
    for (; i < hex.length; i += 2) {
      str += String.fromCharCode(parseInt(hex.substr(i, 2), 16))
    }
    return str
  }

  private a2hex(buffer) {
    // buffer is an ArrayBuffer
    // create a byte array (Uint8Array) that we can use to read the array buffer
    const byteArray = new Uint8Array(buffer)

    // for each element, we want to get its two-digit hexadecimal representation
    const hexParts: string[] = []
    let i = 0
    for (; i < byteArray.length; i++) {
      // convert value to hexadecimal
      const hex = byteArray[i].toString(16)

      // pad with zeros to length 2
      const paddedHex = ('00' + hex).slice(-2)

      // push to array
      hexParts.push(paddedHex)
    }

    // join all the hex values of the elements into a single string
    return hexParts.join('')
  }

  private stringToArrayBuffer(str) {
    const bytes = new Uint8Array(str.length)

    let iii = 0
    for (; iii < str.length; iii++) {
      bytes[iii] = str.charCodeAt(iii)
    }

    return bytes
  }

  private arrayBufferToString(buffer) {
    let str = ''
    let iii = 0
    for (; iii < buffer.byteLength; iii++) {
      str += String.fromCharCode(buffer[iii])
    }

    return str
  }

  private isDefined(variable) {
    return typeof variable !== 'undefined'
  }

  generateKey(params): boolean {
    let length = params.length ? params.length : 256 // 256-bit key by default
    let callback = params.callback

    if (length !== 128 && length !== 192 && length !== 256) {
      this.postMessage({ error: 'invalidKeyLength', callback: callback, func: 'string' })
      return false
    }

    this.cryptoSubtle.generateKey(
      { name: 'AES-CBC', length: length },// can be 128, 192 or 256
      true, // whether the key is extractable (i.e. can be used in exportKey)
      ['encrypt', 'decrypt'] // can be "encrypt", "decrypt", "wrapKey", or "unwrapKey"
    ).catch((error) => {
      this.postMessage({ error: error, callback: callback, func: 'string' })
    }).then((key) => {
      this.cryptoSubtle.exportKey(
        'raw', // can be "jwk" or "raw"
        key // extractable must be true
      ).catch((error) => {
        this.postMessage({ error: error, callback: callback, func: 'string' })
      }).then((keydata) => {
        const key = this.a2hex(new Uint8Array(keydata))
        this.postMessage({ result: key, callback: callback, func: 'string' })
      })
    })

    return true
  }

  generateRandomNumber(params): boolean {
    let length = params.length
    let callback = params.callback

    if (!this.isDefined(length) || isNaN(parseInt(length, 10))) {
      this.postMessage({ error: 'invalidLength', callback: callback, func: 'string' })
      return false
    }

    let randomNumber = this.a2hex(window.crypto.getRandomValues(new Uint8Array(length)))
    this.postMessage({ result: randomNumber, callback: callback, func: 'string' })

    return true
  }

  encrypt(params): boolean {
    let callback = params.callback

    if (!this.isDefined(params.data)) {
      this.postMessage({ error: 'missingData', callback: callback, func: 'data' })
      return false
    }

    let data = atob(params.data)
    let password = params.password
    let key = params.key
    let IV = params.iv
    let salt = ''

    let plaintextData = this.stringToArrayBuffer(data)

    if (this.isDefined(password)) {
      if (password.length === 0) {
        this.postMessage({ error: 'invalidPasswordLength', callback: callback, func: 'data' })
        return false
      }

      let derivedPassword = this.derivePassword(password)
      key = this.stringToArrayBuffer(derivedPassword.key)
      IV = this.stringToArrayBuffer(derivedPassword.iv)
      salt = derivedPassword.salt
    } else if (this.isDefined(key) && this.isDefined(IV)) {
      key = this.stringToArrayBuffer(this.hex2a(key))
      IV = this.stringToArrayBuffer(this.hex2a(IV))

      if (params.key.length !== 32 && params.key.length !== 48 && params.key.length !== 64) {
        this.postMessage({ error: 'invalidKeyLength', callback: callback, func: 'data' })
        return false
      } else if (params.iv.length !== 32) {
        this.postMessage({ error: 'invalidIvLength', callback: callback, func: 'data' })
        return false
      }
    } else {
      this.postMessage({ error: 'missingPasswordKeyOrIv', callback: callback, func: 'data' })
      return false
    }

    this.cryptoSubtle
      .importKey('raw', key, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt'])
      .catch(error => {
        this.postMessage({ error: error, callback: callback, func: 'data' })
      })
      .then(cryptokey => {
        this.cryptoSubtle
          .encrypt(
            { name: 'AES-CBC', iv: IV },
            cryptokey,
            plaintextData
          )
          .catch(error => {
            this.postMessage({ error: error, callback: callback, func: 'data' })
          })
          .then(result => {
            let encryptedData = this.arrayBufferToString(new Uint8Array(result))

            if (salt) {
              encryptedData = this.saltPrefix + salt + encryptedData
            }

            // Base64 encode the data
            encryptedData = btoa(encryptedData)

            this.postMessage({ result: encryptedData, callback: callback, func: 'data' })
          })
      })
    return true
  }

  decrypt(params): boolean {
    let callback = params.callback

    if (!this.isDefined(params.data)) {
      this.postMessage({ error: 'missingData', callback: callback, func: 'data' })
      return false
    }

    let data = atob(params.data)
    let password = params.password
    let key = params.key
    let IV = params.iv
    let salt = ''

    let encryptedData = this.stringToArrayBuffer(data)

    // Check if there's a salt
    if (data.substr(0, 8) === this.saltPrefix) {
      salt = data.substr(8, 8)
      encryptedData = this.stringToArrayBuffer(data.substr(16, data.length))
    }

    if (this.isDefined(password)) {
      if (password.length === 0) {
        this.postMessage({ error: 'invalidPasswordLength', callback: callback, func: 'data' })
        return false
      }

      let derivedPassword = this.derivePassword(password, salt)
      key = this.stringToArrayBuffer(derivedPassword.key)
      IV = this.stringToArrayBuffer(derivedPassword.iv)
    } else if (this.isDefined(key) && this.isDefined(IV)) {
      key = this.stringToArrayBuffer(this.hex2a(key))
      IV = this.stringToArrayBuffer(this.hex2a(IV))

      if (params.key.length !== 32 && params.key.length !== 48 && params.key.length !== 64) {
        this.postMessage({ error: 'invalidKeyLength', callback: callback, func: 'data' })
        return false
      } else if (params.iv.length !== 32) {
        this.postMessage({ error: 'invalidIvLength', callback: callback, func: 'data' })
        return false
      }
    } else {
      this.postMessage({ error: 'missingPasswordKeyOrIv', callback: callback, func: 'data' })
      return false
    }

    this.cryptoSubtle
      .importKey(
        'raw',
        key,
        { name: 'AES-CBC' },
        false,
        ['encrypt', 'decrypt']
      )
      .catch(error => {
        this.postMessage({ error: error, callback: callback, func: 'data' })
      })
      .then(cryptokey => {
        this.cryptoSubtle
          .decrypt(
            { name: 'AES-CBC', iv: IV },
            cryptokey,
            encryptedData
          )
          .catch(error => {
            this.postMessage({ error: error, callback: callback, func: 'data' })
          })
          .then(result => {
            let decryptedData = btoa(this.arrayBufferToString(new Uint8Array(result)))
            this.postMessage({ result: decryptedData, callback: callback, func: 'data' })
          })
      })
    return true
  }

  hash(params) {
    let algorithm = params.algorithm // SHA-1, SHA-256, SHA-384 or SHA-512
    let data = this.stringToArrayBuffer(atob(params.data))
    let callback = params.callback

    this.cryptoSubtle
      .digest(
        { name: algorithm },
        data
      )
      .catch(error => {
        this.postMessage({ error: error, callback: callback, func: 'string' })
      })
      .then(hash => {
        hash = this.a2hex(new Uint8Array(hash))
        this.postMessage({ result: hash, callback: callback, func: 'string' })
      })
  }
}
